import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiAvatar from "components/VuiAvatar";
import VuiBadge from "components/VuiBadge";
import { FaEdit, FaTrash, FaCar, FaGasPump, FaCalendarAlt, FaIdCard } from "react-icons/fa";
import { MdColorLens } from "react-icons/md";
import { FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import GanttTimeline from "components/GanttTimeline";
import { useTranslation } from "react-i18next";

const Section = ({ title, children }) => (
  <VuiBox mb={3}>
    <VuiTypography variant="button" color="text" fontWeight="bold" mb={1}>
      {title}
    </VuiTypography>
    <VuiBox
      sx={{
        background: 'rgba(255,255,255,0.02)',
        borderRadius: '12px',
        p: 2,
      }}
    >
      {children}
    </VuiBox>
  </VuiBox>
);

const StatusBadge = ({ active }) => (
  <VuiBadge
    variant="standard"
    badgeContent={active ? "Active" : "Inactive"}
    color={active ? "success" : "error"}
    size="xs"
    container
    sx={({ palette: { white, success, error }, borders: { borderRadius, borderWidth } }) => ({
      background: active ? success.main : error.main,
      border: `${borderWidth[1]} solid ${active ? success.main : error.main}`,
      borderRadius: borderRadius.md,
      color: white.main,
      ml: 2,
    })}
  />
);

function CarDetailsModal({ open, onClose, car, onEdit, onDelete }) {
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });
  const { t, i18n } = useTranslation();

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightbox.open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setLightbox(l => ({ ...l, index: (l.index - 1 + l.images.length) % l.images.length }));
      } else if (e.key === 'ArrowRight') {
        setLightbox(l => ({ ...l, index: (l.index + 1) % l.images.length }));
      } else if (e.key === 'Escape') {
        setLightbox(l => ({ ...l, open: false }));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightbox.open, lightbox.images.length]);

  if (!car) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: '#191932',
          borderRadius: '24px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <VuiBox
          sx={{
            background: 'linear-gradient(180deg, #191932 0%, #232345 100%)',
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              cursor: 'pointer',
              color: '#fff',
              background: 'rgba(34,34,58,0.8)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
              '&:hover': {
                background: '#2196f3',
                color: '#fff',
              },
            }}
            onClick={onClose}
            title={t('common.cancel')}
          >
            <FiX size={24} />
          </Box>
        </VuiBox>
        <Avatar
          src={car.images?.[0]}
          alt={`${car.manufacturer} ${car.model}`}
          sx={{ width: 72, height: 72, border: '3px solid #353570' }}
        />
        <VuiBox display="flex" alignItems="center" gap={2} flex={1} sx={{ mb: '18px' }}>
          <VuiBox display="flex" flexDirection="column" gap={1}>
            <VuiTypography variant="h4" color="white" fontWeight="bold" mb={0.5}>
              {car.manufacturer} {car.model} <span style={{fontWeight:400, fontSize: '22px', opacity: 0.7}}>({car.year})</span>
            </VuiTypography>
            <VuiTypography variant="caption" color="text" fontWeight="regular">
              {car.plate_number}
            </VuiTypography>
          </VuiBox>
          <Box sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: `#${car.color}`,
            border: '2px solid #fff',
            boxShadow: '0 0 4px #0003',
            ml: 2,
          }} />
        </VuiBox>

        <Box
          sx={{
            background: 'rgba(34, 34, 58, 0.85)',
            borderRadius: '24px',
            p: { xs: 2, sm: 4 },
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minHeight: 80,
          }}
        >
          <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.6, fontSize: 14, mr: 2 }}>
            {t('car_table.fuel_consumption')}
          </VuiTypography>
          <VuiTypography color="white" sx={{ fontSize: 18, fontWeight: 400, ml: 1 }}>
            {car.fuel_consumption_per_100Km}
          </VuiTypography>
          <VuiTypography color="white" fontWeight="bold" sx={{ fontSize: 18, ml: 0.5 }}>
            {t('car_table.fuel_consumption_unit')}
          </VuiTypography>
        </Box>

        {car.images && car.images.length > 0 && (
          <Section title={t('car_table.car_images')}>
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', py: 1 }}>
              {car.images.map((url, idx) => (
                <img
                  key={idx}
                  src={url}
                  alt={`Car ${idx + 1}`}
                  style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 12, border: '2px solid #353570', background: '#232345', cursor: 'pointer' }}
                  onClick={() => setLightbox({ open: true, images: car.images, index: idx })}
                />
              ))}
            </Box>
          </Section>
        )}

        <Section title={t('car_table.identification_marks')}>
          <VuiTypography variant="button" color="white" sx={{ whiteSpace: 'pre-line' }}>
            {car.identification_marks || t('car_table.none')}
          </VuiTypography>
        </Section>
      </DialogContent>

      {/* Lightbox Modal for images */}
      <Dialog
        open={lightbox.open}
        onClose={() => setLightbox({ ...lightbox, open: false })}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(0,0,0,0)',
            borderRadius: 0,
            m: 0,
            p: 0,
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            maxHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'visible',
          },
        }}
      >
        <DialogContent sx={{
          width: '100vw',
          height: '100vh',
          p: 0,
          m: 0,
          overflow: 'visible',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          position: 'relative',
        }}>
          {/* Close button */}
          <Box
            sx={{
              position: 'absolute',
              top: 24,
              right: 24,
              zIndex: 2000,
              background: 'rgba(34,34,58,0.8)',
              border: 'none',
              borderRadius: '50%',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
            onClick={() => setLightbox({ ...lightbox, open: false })}
            title="Close preview"
          >
            <FiX size={28} />
          </Box>
          {/* Left arrow */}
          {lightbox.images.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                left: 24,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2000,
                background: 'rgba(34,34,58,0.8)',
                borderRadius: '50%',
                width: 44,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
              onClick={() => setLightbox(l => ({ ...l, index: (l.index - 1 + l.images.length) % l.images.length }))}
              title="Previous"
            >
              <FiChevronLeft size={28} />
            </Box>
          )}
          {/* Right arrow */}
          {lightbox.images.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                right: 24,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2000,
                background: 'rgba(34,34,58,0.8)',
                borderRadius: '50%',
                width: 44,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
              onClick={() => setLightbox(l => ({ ...l, index: (l.index + 1) % l.images.length }))}
              title="Next"
            >
              <FiChevronRight size={28} />
            </Box>
          )}
          {/* Main image */}
          <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {lightbox.images[lightbox.index] && (
              <img
                src={lightbox.images[lightbox.index]}
                alt={`Car ${lightbox.index + 1}`}
                style={{
                  maxWidth: 'calc(100vw - 64px)',
                  maxHeight: 'calc(100vh - 64px)',
                  borderRadius: 16,
                  boxShadow: '0 4px 32px 0 rgba(0,0,0,0.25)',
                  objectFit: 'contain',
                  background: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'block',
                }}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default CarDetailsModal; 